import { ApiErrorResult, ApiTextAreaErrorDetail } from '/#/axios'

import forEach from 'lodash-es/forEach'
import has from 'lodash-es/has'
import isArray from 'lodash-es/isArray'
import isFunction from 'lodash-es/isFunction'
import isObject from 'lodash-es/isObject'
import isUndefined from 'lodash-es/isUndefined'

/**
 * Add the object as a parameter to the URL
 * @param baseUrl url
 * @param obj
 * @returns {string}
 * eg:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = ''
  Object.keys(obj).forEach((key) => {
    parameters += key + '=' + encodeURIComponent(obj[key]) + '&'
  })

  parameters = parameters.replace(/&$/, '')
  return /\?$/.test(baseUrl)
    ? baseUrl + parameters
    : baseUrl.replace(/\/?$/, '?') + parameters
}

/**
 * @description:  Flatten the tree structure array
 * @param collection
 * @param flattened
 */
export function flatten(collection: any[], flattened?: any[]): any[] {
  if (!flattened) {
    flattened = []
  }
  forEach(collection, (obj: any) => {
    flattened!.push(obj)
    if (has(obj, 'children')) {
      flatten(obj.children, flattened)
    }
  })
  return flattened
}

/**
 * Returns a string with the first letter capitalized
 * @param {string} string
 * @returns {string}
 */
export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Returns a converted array object for the select component.
 * If you give a matcherArray, it will disable the values that are not in the array.
 * @param array
 * @param formatLabel {Function}
 * @constructor
 *
 * @returns {Array}
 */
export function convertArrayToSelectObject(
  array: Array<string | number>,
  formatLabel?: Function,
) {
  if (formatLabel && isFunction(formatLabel)) {
    return array.map((value) => ({ label: formatLabel(value), value: value }))
  }
  return array.map((value) => ({ label: value, value: value }))
}

/**
 * @description: It returns the details by parsing and taking the field errors that occur on the API side.
 * @param {Recordable} error
 *
 * @returns {reason, statusCode, fieldErrors, nonFieldError,errorData}
 */
export const parseApiErrors = (error: Recordable) => {
  if (error.response && isObject(error.response.data)) {
    const { data } = error.response

    const reason: Partial<ApiErrorResult> = data?.reason
    const statusCode: Partial<ApiErrorResult> = data?.status_code

    let nonFieldError: string | undefined
    const fieldErrors: Recordable = {}

    let textareaErrors: ApiTextAreaErrorDetail[] | undefined

    // Get the field name and set errorList with the error message.
    if (isArray(data?.errors)) {
      data?.errors?.forEach((error: Recordable) => {
        // If the error is not related to a field, set the nonFieldErrors.
        if (error.attr === 'non_field_errors' || error.attr === null)
          nonFieldError = error.detail
        else fieldErrors[error.attr] = error.detail
      })
    } else if (!isUndefined(data.errors.errors)) textareaErrors = data.errors.errors

    return {
      reason,
      statusCode,
      fieldErrors,
      nonFieldError,
      errorData: data,
      textareaErrors,
    }
  }
  return {
    reason: {},
    statusCode: 0,
    fieldErrors: {},
    nonFieldError: '',
    textareaErrors: undefined,
    errorData: error,
  }
}

/**
 * @method flattenTree
 * @description Converts a hierarchical tree structure into a flat array while preserving parent-child relationships.
 * Adds meta-information to each item: level, isParent, hasChildren, parentId, treeGroup.
 */
export function flattenTree(
  tree: Recordable[],
  treeKey = 'children',
  valueField = 'id',
  labelField = 'name',
  parentId: string | number | null = null,
  level = 0,
  parentValue: string | number | null = null,
): Recordable[] {
  if (!Array.isArray(tree) || tree.length === 0) return []

  const result: Recordable[] = []

  tree.forEach((item) => {
    const value = item[valueField]
    const label = item[labelField]
    const children = item[treeKey]

    const hasChildren = Array.isArray(children) && children.length > 0
    const isParent = level === 0

    const itemLevel = level
    const itemParentId = isParent ? null : parentId
    const itemTreeGroup = isParent ? value : parentValue || value

    const flatOption: Recordable = {
      ...item,
      value,
      label,
      level: itemLevel,
      isParent: isParent,
      hasChildren,
      parentId: itemParentId,
      treeGroup: itemTreeGroup,
    }

    result.push(flatOption)

    if (hasChildren) {
      const childOptions = flattenTree(
        children,
        treeKey,
        valueField,
        labelField,
        value,
        itemLevel + 1,
        value,
      )

      result.push(...childOptions)
    }
  })

  return result
}

/**
 * @method isFunctionParamTakesObject
 * @description Does check for a function is require params as object or not.
 * -> apiFn.length returns params length for fn
 * -> apiFn.prototype for function construction params
 *
 * @param fn {Function} Holds fn.
 */
export function isFunctionParamTakesObject(fn: Function) {
  return !(!isFunction(fn) || !fn.length || fn.prototype !== 'object')
}
